import type { FunctionComponent, ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

import { useArticleRenderContext } from "~/components/article/article-render/contexts";
import { useArticleSpeechUtils } from "~/components/article/article-speech/article-speech-player/hooks";
import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";

type ContextValue = {
  isPlayed: boolean;
  onClick?: () => void;
};

type Props = {
  children: ReactNode;
  onClick?: () => void;
};

const ArticleSpeechInlineWidgetContext = createContext<ContextValue>({
  isPlayed: false,
});

export const ArticleSpeechInlineWidgetContextProvider: FunctionComponent<Props> = ({
  children,
  onClick,
}) => {
  const [isPlayed, setIsPlayed] = useState(false);
  const { articleSpeechifyPlayer } = useArticleSpeechContext();
  const { articleElementReference } = useArticleRenderContext();
  const { checkIsCurrentArticlePlaying } = useArticleSpeechUtils();

  useEffect(() => {
    if (!articleSpeechifyPlayer || !checkIsCurrentArticlePlaying(articleElementReference?.current))
      return;

    const run = async () => {
      const playerState = await articleSpeechifyPlayer.getPlayerState();
      setIsPlayed(playerState.playing);
    };
    void run();

    return () => {
      setIsPlayed(false);
    };
  }, [articleElementReference, articleSpeechifyPlayer, checkIsCurrentArticlePlaying]);

  useEffect(() => {
    if (!articleSpeechifyPlayer || !checkIsCurrentArticlePlaying(articleElementReference?.current))
      return;

    const removeEventListener = articleSpeechifyPlayer.addEventListener(event => {
      switch (event.type) {
        case "play":
        case "resume":
          setIsPlayed(true);
          return;
        case "pause":
          setIsPlayed(false);
          return;
      }
    });
    return () => removeEventListener();
  }, [articleElementReference, articleSpeechifyPlayer, checkIsCurrentArticlePlaying]);

  return (
    <ArticleSpeechInlineWidgetContext.Provider
      value={{
        isPlayed,
        onClick,
      }}
    >
      {children}
    </ArticleSpeechInlineWidgetContext.Provider>
  );
};

export const useArticleSpeechInlineWidgetContext = () =>
  useContext(ArticleSpeechInlineWidgetContext);
